import { Skeleton } from '@/components/ui';
import { useCan } from '@/core/session';

/* =============================================================================
 * The dashboard, before it has arrived
 * -----------------------------------------------------------------------------
 * ui.tsx already states the rule this exists to keep: a skeleton is shaped like
 * the thing that replaces it, or the page appears to load twice — once as a
 * rectangle and once as itself.
 *
 * The dashboard had stopped keeping it. Opening it showed three different
 * layouts in a row:
 *
 *   PageLoading         two heading bars, FOUR tiles, one 320px slab
 *   the loading branch  the real heading, FIVE tiles, and nothing underneath
 *   the dashboard       five figures, two rows of three panels, a tile strip
 *
 * None of the three shared a shape with the next, so the screen assembled
 * itself twice and jumped both times. The middle one was the worst of them: the
 * panels below the figures are each `data?.thing && <Panel …>`, so while the
 * query was in flight the page simply ended after the tiles, and everything
 * below arrived at once into empty space.
 *
 * This is the one shape, used for both — the Suspense fallback while the chunk
 * downloads and the loading state while the query runs. Which is also why it
 * pulls in nothing heavy: it stands in for the chunk that holds the charts, so
 * it cannot be inside it.
 *
 * -----------------------------------------------------------------------------
 * IT PROMISES SLIGHTLY LESS THAN IT EXPECTS TO GET
 * -----------------------------------------------------------------------------
 * dashboard_summary builds each panel only for a caller holding its grant, so
 * what actually arrives differs by role — a supervisor gets no payroll and no
 * WPS at all. A skeleton that draws them anyway would make the page SHRINK when
 * the data lands, and content pulling upwards reads far worse than content
 * arriving below.
 *
 * So the two money panels are drawn only where the same grant the database
 * checks is held, and the quick-access strip at the bottom is left out
 * entirely: its tiles are filtered per grant and there is no honest count to
 * draw. Under-promising leaves the page growing downwards, which reads as
 * loading rather than as a fault.
 * ========================================================================== */

/** A titled panel's worth of grey. The title bar matches Panel's own heading. */
function PanelSkeleton({ height }: { height: number }) {
  return (
    <section className="bg-surface border border-line rounded-card shadow-card p-4">
      <Skeleton className="h-[15px] w-40 mb-4" />
      {/* Height varies by row, so it is set rather than named — the class list
          is the same one Skeleton applies. */}
      <div className="skeleton rounded-card" style={{ height }} aria-hidden="true" />
    </section>
  );
}

export function DashboardSkeleton({ header = true }: {
  /**
   * Draw the greeting and the filters.
   *
   * True as the route's fallback, where nothing has rendered yet. False from
   * inside the dashboard, which has already drawn its own header from the
   * session — the name and the date range are known long before the figures
   * are, and redrawing them as grey would be a step backwards.
   */
  header?: boolean;
}) {
  const can = useCan();
  const money = can('payroll.view');

  return (
    <div className="flex flex-col gap-5" aria-busy="true">
      {header && (
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <Skeleton className="h-[26px] w-72" />
            <Skeleton className="h-[13px] w-56 mt-2" />
            <Skeleton className="h-[12px] w-48 mt-2.5" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-[38px] w-[150px]" />
            <Skeleton className="h-[38px] w-[190px]" />
          </div>
        </div>
      )}

      {/* The five figures. */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-5">
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-[104px] rounded-card" />
        ))}
      </div>

      {/* Attendance, and the two that are only drawn for somebody who will be
          sent them. The row keeps lg:grid-cols-3 either way, so a single panel
          sits at a third width exactly as the real one does. */}
      <div className="grid gap-4 lg:grid-cols-3">
        <PanelSkeleton height={240} />
        {money && <PanelSkeleton height={240} />}
        {money && <PanelSkeleton height={240} />}
      </div>

      {/* Leave, job cost, and what needs somebody. */}
      <div className="grid gap-4 lg:grid-cols-3">
        <PanelSkeleton height={168} />
        <PanelSkeleton height={168} />
        <PanelSkeleton height={168} />
      </div>
    </div>
  );
}
